/**
 * Pure builder for the partner-route request headers attached by
 * `apiRequest` / `getQueryFn` in `client/src/lib/queryClient.ts`. Lives
 * in `shared/` so it can be unit-tested in Node without polyfilling a
 * DOM, and so the contract that `requirePartner` (server middleware)
 * relies on — namely "every write to /api/partner/* MUST carry an
 * `x-restaurant-id` header whenever a partner restaurant id is on
 * record" — is locked by a real test instead of an ad-hoc inline
 * snippet.
 *
 * Behavior locked here:
 *   - `x-partner-user-id` is set iff `partnerUserId` is a non-empty string.
 *   - `x-restaurant-id`   is set iff `restaurantId`   is a non-empty string.
 *   - `x-eatzy-token`     is set iff `eatzyToken`     is a non-empty string.
 *   - Empty / null / undefined values are NEVER stringified into a header.
 *     This matters because `requirePartner` rejects "" and "null" the same
 *     way it rejects a missing header.
 */
export interface PartnerHeaderInputs {
  partnerUserId?: string | null;
  restaurantId?: string | null;
  eatzyToken?: string | null;
}

export function buildPartnerRequestHeaders(
  inputs: PartnerHeaderInputs
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (inputs.partnerUserId && inputs.partnerUserId.length > 0) {
    headers["x-partner-user-id"] = inputs.partnerUserId;
  }
  if (inputs.restaurantId && inputs.restaurantId.length > 0) {
    headers["x-restaurant-id"] = inputs.restaurantId;
  }
  if (inputs.eatzyToken && inputs.eatzyToken.length > 0) {
    headers["x-eatzy-token"] = inputs.eatzyToken;
  }
  return headers;
}
